// LeftPanel extracted from OfferPageBuilderSimple.js lines 12327-13199
// Mobile Drawer + Desktop left sidebar with element library and saved elements
import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  SwipeableDrawer,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ViewModule as TemplateIcon,
  Save as SaveIcon,
  Phone as PhoneIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { useOfferBuilder } from '../OfferBuilderContext';

const LeftPanel = ({ children }) => {
  const {
    isMobile,
    mobileDrawerOpen,
    setMobileDrawerOpen,
    templatesOnly,
    hideCustomElements,
    hideLoadTemplate,
    customCategories,
    selectedCustomCategory,
    setSelectedCustomCategory,
    setSelectedCustomSubtype,
    setSelectedVariantKey,
    customElementsLibrary,
    selectedCustomSubtype,
    selectedVariantKey,
    setSelectedTemplateId,
    filteredTemplates,
    loadPreBuiltTemplate,
    addCustomElement,
    selectedTemplateId,
    elementLibrary,
    addElement,
    drawerOpen,
    setDrawerOpen,
    customElementsOnly,
    isBrandsPage,
    elements,
    pages,
    currentPageIndex,
    paginationEnabled,
    previewDevice,
    setPreviewDevice,
    showUndoNotification,
    undoDelete,
    dismissUndoNotification,
    isUpdatingElement,
    currentElementName,
    setShowSaveElementDialog,
    // missing from original extract
    templateSelectionOnly,
    selectedElement,
    selectedBrand,
    selectedOffer,
    handleDragStart,
    addPage,
    deletePage,
    deletedElementsHistory,
    saveCurrentElement,
    setShowLayoutSelector,
    setShowSaveDialog,
    setShowTemplatesList,
    currentTemplateId,
    createNewTemplate,
    setConfirmDialogTitle,
    setConfirmDialogMessage,
    setConfirmAction,
    setShowConfirmDialog,
    setShowBrandOfferAlert,
  } = useOfferBuilder();

  return (
    <>
        {/* Mobile Drawer */}
        {isMobile ? (
          <SwipeableDrawer
            anchor="bottom"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            onOpen={() => setMobileDrawerOpen(true)}
            PaperProps={{
              sx: { maxHeight: '70vh', borderRadius: '16px 16px 0 0' }
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">🧩 Elements & Templates</Typography>
                <IconButton onClick={() => setMobileDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Mobile element library */}
              <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {!templatesOnly && (
                  <>
                    {/* Custom Elements Section - Only show if not hidden */}
                    {!hideCustomElements && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 3, minHeight: 200 }}>
                        {/* Left: Offer Element type */}
                        <Box sx={{ minWidth: 120, borderRight: '2px solid #e0e0e0', pr: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600, textAlign: 'center' }}>Offer Element type</Typography>
                          {customCategories.map(category => (
                            <Button
                              key={category}
                              variant={selectedCustomCategory === category ? 'contained' : 'outlined'}
                              color={selectedCustomCategory === category ? 'warning' : 'primary'}
                              fullWidth
                              sx={{ mb: 1, justifyContent: 'flex-start', fontWeight: selectedCustomCategory === category ? 700 : 400 }}
                              onClick={() => {
                                setSelectedCustomCategory(category);
                                setSelectedCustomSubtype(null);
                                setSelectedVariantKey(null);
                              }}
                            >
                              {category}
                            </Button>
                          ))}
                        </Box>
                        {/* Middle: Offer Element subtype */}
                        <Box sx={{ minWidth: 120, borderRight: selectedCustomSubtype ? '2px solid #e0e0e0' : 'none', pr: selectedCustomSubtype ? 1 : 0 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600, textAlign: 'center' }}>Offer Element subtype</Typography>
                          {customElementsLibrary.filter(item => item.category === selectedCustomCategory).map(item => (
                            <Button
                              key={item.id}
                              variant={selectedCustomSubtype?.id === item.id ? 'contained' : 'outlined'}
                              color={selectedCustomSubtype?.id === item.id ? 'warning' : (item.label === 'Button w/text' ? 'warning' : 'primary')}
                              fullWidth
                              sx={{ mb: 1, justifyContent: 'flex-start', fontWeight: 500 }}
                              onClick={() => {
                                setSelectedCustomSubtype(item);
                                setSelectedVariantKey(null);
                                setSelectedTemplateId(null);
                              }}
                              disabled={item.elements.length === 0}
                            >
                              {item.label}
                            </Button>
                          ))}
                        </Box>
                        {/* Right: Variants */}
                        {selectedCustomSubtype && selectedCustomSubtype.elements.length > 0 && selectedCustomSubtype.elements[0].variants && (
                          <Box sx={{ flex: 1, pl: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600, textAlign: 'center' }}>Select a template</Typography>

                            {/* Show prebuilt templates for this subtype (Mobile) */}
                            {(() => {
                              // Get templates based on type and subtype
                              const relevantTemplates = filteredTemplates.filter(t => !t.isPreBuilt);

                              // Debug: Log all templates to identify duplicates
                              console.log('🔍 Mobile Template Debug:', {
                                selectedCategory: selectedCustomCategory,
                                selectedSubtype: selectedCustomSubtype.label,
                                filteredTemplates: filteredTemplates.map(t => ({ name: t.name, id: t._id, type: t.type, subtype: t.subtype }))
                              });

                              // Remove duplicates by ID
                              const uniqueTemplates = relevantTemplates.filter((template, index, self) =>
                                index === self.findIndex(t => t._id === template._id)
                              );

                              console.log('🔍 Mobile Unique Templates:', {
                                original: relevantTemplates.length,
                                unique: uniqueTemplates.length
                              });

                              return (
                                <Box key="mobile-templates">
                                  {/* Templates */}
                                  {uniqueTemplates.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1, fontSize: '0.65rem', color: 'primary.main', fontWeight: 600 }}>
                                        🎨 {selectedCustomSubtype.label} Templates
                                      </Typography>
                                      {uniqueTemplates.map(template => (
                                        <Button
                                          key={template._id}
                                          variant={selectedTemplateId === template._id ? 'contained' : 'outlined'}
                                          color="primary"
                                          fullWidth
                                          sx={{
                                            mb: 1,
                                            justifyContent: 'flex-start',
                                            fontWeight: 500,
                                            borderColor: '#1976d2',
                                            color: selectedTemplateId === template._id ? '#ffffff' : '#1976d2',
                                            backgroundColor: selectedTemplateId === template._id ? '#1976d2' : undefined,
                                            '&:hover': {
                                              backgroundColor: selectedTemplateId === template._id ? '#1565c0' : '#e3f2fd',
                                              borderColor: '#1565c0'
                                            }
                                          }}
                                          onClick={() => {
                                            // Load the prebuilt template
                                            loadPreBuiltTemplate(template);
                                            setMobileDrawerOpen(false);
                                          }}
                                        >
                                          {template.name}
                                        </Button>
                                      ))}
                                    </Box>
                                  )}

                                  {/* Empty state when no templates */}
                                  {uniqueTemplates.length === 0 && (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                        No {selectedCustomSubtype.label} templates yet
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                        Drag your templates to the conversion area below to create prebuilt templates
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })()}

                            {/* Existing variants */}
                            {Object.keys(selectedCustomSubtype.elements[0].variants).map(variantKey => {
                              const variantContent = selectedCustomSubtype.elements[0].variants[variantKey];

                              // Debug log for variant content
                              console.log('🔍 Mobile Variant Debug:', {
                                subtype: selectedCustomSubtype.label,
                                variantKey: variantKey,
                                variantContent: variantContent,
                                variantContentType: typeof variantContent,
                                variantContentKeys: variantContent ? Object.keys(variantContent) : []
                              });

                              return (
                                <Button
                                  key={variantKey}
                                  variant={selectedVariantKey === variantKey ? 'contained' : 'outlined'}
                                  color={selectedVariantKey === variantKey ? 'primary' : 'primary'}
                                  fullWidth
                                  sx={{
                                    mb: 1,
                                    justifyContent: 'flex-start',
                                    fontWeight: 500,
                                    backgroundColor: selectedVariantKey === variantKey ? '#1976d2' : undefined,
                                    color: selectedVariantKey === variantKey ? '#ffffff' : undefined,
                                    '&:hover': {
                                      backgroundColor: selectedVariantKey === variantKey ? '#1565c0' : undefined
                                    }
                                  }}
                                  onClick={() => {
                                    console.log('🎯 Mobile Variant Click:', {
                                      subtype: selectedCustomSubtype.label,
                                      variantKey: variantKey,
                                      variantContent: variantContent,
                                      elementVariants: selectedCustomSubtype.elements[0].variants
                                    });

                                    setSelectedVariantKey(variantKey);
                                    setSelectedTemplateId(null); // Clear template selection

                                    // Create deep copy of variant content to prevent reference issues
                                    const contentCopy = variantContent ? JSON.parse(JSON.stringify(variantContent)) : {};

                                    const elementWithVariant = {
                                      ...selectedCustomSubtype,
                                      elements: selectedCustomSubtype.elements.map(el => ({
                                        ...el,
                                        variant: variantKey,
                                        content: contentCopy
                                      }))
                                    };
                                    addCustomElement(elementWithVariant);
                                    setMobileDrawerOpen(false);
                                  }}
                                >
                                  {variantKey.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </Button>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Regular Elements Section - Only show if not customElementsOnly */}
                    {!customElementsOnly && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>🧩 Elements</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                          {elementLibrary.map((item) => (
                            <Button
                              key={item.type}
                              variant="outlined"
                              size="small"
                              startIcon={item.icon ? <item.icon /> : <AddIcon />}
                              onClick={() => {
                                addElement(item.type);
                                setMobileDrawerOpen(false);
                              }}
                              sx={{ flexDirection: 'column', p: 1, minHeight: '60px' }}
                            >
                              <Typography variant="caption">{item.label}</Typography>
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}


              </Box>
            </Box>
          </SwipeableDrawer>
        ) : (
          // Desktop drawer - only render in template creation context
          !templatesOnly ? <Box sx={{
            width: drawerOpen ? (customElementsOnly ? 650 : 145) : 0,
            flexShrink: 0,
            overflow: 'hidden',
            transition: 'width 0.3s ease'
          }}>
            <Drawer
              variant="persistent"
              anchor="left"
              open={drawerOpen}
              sx={{
                width: customElementsOnly ? 650 : 145, // Compact but readable
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: customElementsOnly ? 650 : 145,
                  position: 'relative',
                  height: '100%',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': { width: '5px' },
                  '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                  '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '10px' },
                  '&::-webkit-scrollbar-thumb:hover': { background: '#999' }
                }
              }}
            >
              <Box sx={{ p: 1, pb: 8 }}>
                {!templatesOnly && (
                  <>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '0.75rem', borderBottom: '1px solid #e0e0e0', pb: 0.5, fontWeight: 'bold' }}>
                        📐 Layouts
                      </Typography>
                      <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setShowLayoutSelector(true)}
                        sx={{ fontSize: '0.7rem', py: 0.5, px: 1, minHeight: '32px', '& .MuiButton-startIcon': { marginRight: '4px', '& > *:nth-of-type(1)': { fontSize: '0.85rem' } } }}
                      >
                        Add Layout Container
                      </Button>
                    </Box>
                    {/* Custom Elements Section - Only show if not hidden */}
                    {!hideCustomElements && (
                      <Box sx={{
                        display: 'flex',
                        gap: 1,
                        mb: 3,
                        minHeight: 500,
                        width: '100%',
                        overflowX: 'auto',
                        pb: 1,
                        '&::-webkit-scrollbar': { height: '6px' },
                        '&::-webkit-scrollbar-thumb': { background: '#ddd', borderRadius: '10px' }
                      }}>
                        {/* Left: Offer Element type */}
                        <Box sx={{
                          flex: 0.8,
                          minWidth: customElementsOnly ? 160 : 140,
                          borderRight: '1px solid #e0e0e0',
                          pr: 1,
                          maxHeight: 750,
                          overflowY: 'auto',
                          '&::-webkit-scrollbar': { width: '4px' },
                          '&::-webkit-scrollbar-thumb': { background: '#eee', borderRadius: '10px' }
                        }}>
                          <Typography variant="caption" sx={{
                            mb: 1,
                            color: 'primary.main',
                            fontWeight: 600,
                            textAlign: 'center',
                            display: 'block',
                            fontSize: '0.8rem',
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'white',
                            zIndex: 1,
                            py: 0.5,
                            borderBottom: '1px solid #f0f0f0'
                          }}>Offer Element type</Typography>
                          {customCategories.map(category => (
                            <Button
                              key={category}
                              variant={selectedCustomCategory === category ? 'contained' : 'outlined'}
                              color={selectedCustomCategory === category ? 'warning' : 'primary'}
                              fullWidth
                              size="small"
                              sx={{
                                mb: 0.5,
                                justifyContent: 'flex-start',
                                fontWeight: selectedCustomCategory === category ? 600 : 400,
                                fontSize: '0.7rem',
                                minHeight: '28px',
                                py: 0.5
                              }}
                              onClick={() => {
                                setSelectedCustomCategory(category);
                                setSelectedCustomSubtype(null);
                                setSelectedVariantKey(null);
                                setSelectedTemplateId(null);
                              }}
                            >
                              {category}
                            </Button>
                          ))}
                        </Box>

                        {/* Middle: Offer Element subtype - Always visible */}
                        <Box sx={{
                          flex: 0.9,
                          minWidth: customElementsOnly ? 180 : 160,
                          borderRight: '1px solid #e0e0e0',
                          pr: 1,
                          maxHeight: 750,
                          overflowY: 'auto',
                          '&::-webkit-scrollbar': { width: '4px' },
                          '&::-webkit-scrollbar-thumb': { background: '#eee', borderRadius: '10px' }
                        }}>
                          <Typography variant="caption" sx={{
                            mb: 1,
                            color: 'primary.main',
                            fontWeight: 600,
                            textAlign: 'center',
                            display: 'block',
                            fontSize: '0.8rem',
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'white',
                            zIndex: 1,
                            py: 0.5,
                            borderBottom: '1px solid #f0f0f0'
                          }}>Offer Element subtype</Typography>
                          {selectedCustomCategory ? (
                            customElementsLibrary.filter(item => item.category === selectedCustomCategory).map(item => (
                              <Button
                                key={item.id}
                                variant={selectedCustomSubtype?.id === item.id ? 'contained' : 'outlined'}
                                color={selectedCustomSubtype?.id === item.id ? 'warning' : (item.label === 'Button w/text' ? 'warning' : 'primary')}
                                fullWidth
                                size="small"
                                sx={{
                                  mb: 0.5,
                                  justifyContent: 'flex-start',
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  minHeight: '28px',
                                  py: 0.5
                                }}
                                onClick={() => {
                                  setSelectedCustomSubtype(item);
                                  setSelectedVariantKey(null);
                                  setSelectedTemplateId(null);
                                }}
                                disabled={item.elements.length === 0}
                              >
                                {item.label}
                              </Button>
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2, fontSize: '0.65rem' }}>
                              Select a category to see subtypes
                            </Typography>
                          )}
                        </Box>

                        {/* Right: Variants - Always visible */}
                        <Box sx={{
                          flex: 1.3,
                          minWidth: customElementsOnly ? 220 : 160,
                          pr: 1,
                          maxHeight: 750,
                          overflowY: 'auto',
                          '&::-webkit-scrollbar': { width: '4px' },
                          '&::-webkit-scrollbar-thumb': { background: '#eee', borderRadius: '10px' }
                        }}>
                          <Typography variant="caption" sx={{
                            mb: 1,
                            color: 'primary.main',
                            fontWeight: 600,
                            textAlign: 'center',
                            display: 'block',
                            fontSize: '0.8rem',
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'white',
                            zIndex: 1,
                            py: 0.5,
                            borderBottom: '1px solid #f0f0f0'
                          }}>Select a template</Typography>

                          {/* Show prebuilt templates for this subtype */}
                          {(selectedCustomCategory && filteredTemplates.length > 0) && (() => {
                            // Get templates based on type and subtype
                            const relevantTemplates = filteredTemplates.filter(t => !t.isPreBuilt);

                            // Debug: Log all templates to identify duplicates
                            console.log('🔍 Desktop Template Debug:', {
                              selectedCategory: selectedCustomCategory,
                              selectedSubtype: selectedCustomSubtype.label,
                              filteredTemplates: filteredTemplates.map(t => ({ name: t.name, id: t._id, type: t.type, subtype: t.subtype, isPreBuilt: t.isPreBuilt }))
                            });

                            console.log('🔍 Relevant Templates (non-prebuilt):', relevantTemplates.map(t => ({ name: t.name, id: t._id, type: t.type, subtype: t.subtype })));

                            // Remove duplicates by ID
                            const uniqueTemplates = relevantTemplates.filter((template, index, self) =>
                              index === self.findIndex(t => t._id === template._id)
                            );

                            console.log('🔍 Desktop Unique Templates:', {
                              original: relevantTemplates.length,
                              unique: uniqueTemplates.length,
                              templates: uniqueTemplates.map(t => ({ name: t.name, id: t._id, type: t.type, subtype: t.subtype }))
                            });

                            return (
                              <Box key="desktop-templates">
                                {/* Templates */}
                                {uniqueTemplates.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1, fontSize: '0.65rem', color: 'primary.main', fontWeight: 600 }}>
                                      🎨 {selectedCustomSubtype.label} Templates
                                    </Typography>
                                    {uniqueTemplates.map(template => (
                                      <Button
                                        key={template._id}
                                        variant={selectedTemplateId === template._id ? 'contained' : 'outlined'}
                                        color="primary"
                                        fullWidth
                                        size="small"
                                        sx={{
                                          mb: 0.5,
                                          justifyContent: 'flex-start',
                                          fontWeight: 500,
                                          fontSize: '0.7rem',
                                          minHeight: '28px',
                                          py: 0.5,
                                          borderColor: '#1976d2',
                                          color: selectedTemplateId === template._id ? '#ffffff' : '#1976d2',
                                          backgroundColor: selectedTemplateId === template._id ? '#1976d2' : undefined,
                                          '&:hover': {
                                            backgroundColor: selectedTemplateId === template._id ? '#1565c0' : '#e3f2fd',
                                            borderColor: '#1565c0'
                                          }
                                        }}
                                        onClick={() => {
                                          // Load the prebuilt template
                                          loadPreBuiltTemplate(template);
                                        }}
                                      >
                                        {template.name}
                                      </Button>
                                    ))}
                                  </Box>
                                )}

                                {/* Empty state when no templates */}
                                {uniqueTemplates.length === 0 && (
                                  <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                      No {selectedCustomSubtype.label} templates yet
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                      Drag your templates to the conversion area below to create prebuilt templates
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}



                          {/* Default message when no templates available */}
                          {!(selectedCustomCategory && filteredTemplates.length > 0) && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1, mb: 2, fontSize: '0.65rem' }}>
                                {selectedCustomCategory
                                  ? 'No templates available for this category'
                                  : 'Select a category to see templates'
                                }
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Regular Elements Section - Only show if not customElementsOnly */}
                    {!customElementsOnly && (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 1.5, fontSize: '0.75rem', borderBottom: '1px solid #e0e0e0', pb: 0.5, fontWeight: 'bold' }}>
                          🧩 Elements
                        </Typography>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.3,
                          maxHeight: 600,
                          overflowY: 'auto',
                          pr: 0.5,
                          '&::-webkit-scrollbar': { width: '3px' },
                          '&::-webkit-scrollbar-thumb': { background: '#eee', borderRadius: '10px' }
                        }}>
                          {elementLibrary.map((item) => (
                            <Button
                              key={item.type}
                              variant="outlined"
                              size="small"
                              startIcon={item.icon ? <item.icon /> : <AddIcon />}
                              onClick={() => addElement(item.type)}
                              onDragStart={(e) => handleDragStart(e, item.type)}
                              draggable
                              sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                fontSize: '0.72rem',
                                py: 0.6,
                                px: 1,
                                height: 'auto',
                                minHeight: '34px',
                                lineHeight: 1.2,
                                whiteSpace: 'normal',
                                textAlign: 'left',
                                '& .MuiButton-startIcon': {
                                  marginRight: '4px',
                                  '& > *:nth-of-type(1)': { fontSize: '0.85rem' }
                                }
                              }}
                            >
                              {item.label}
                            </Button>
                          ))}
                        </Box>

                      </>
                    )}
                  </>
                )}


              </Box>
            </Drawer>
          </Box> : null
        )}

        {/* Editor Column */}
        <Box sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid #e0e0e0',
          maxWidth: (isBrandsPage && templatesOnly) ? '100%' : '100%',
          minWidth: 0,
          overflow: 'hidden'
        }}>
          {/* Desktop Toolbar */}
          <Box sx={{
            p: { xs: 0.5, md: 0.5 },
            borderBottom: '1px solid #e0e0e0',
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'nowrap',
            backgroundColor: '#ffffff',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <Typography variant="overline" sx={{ fontWeight: 800, color: 'primary.main', mr: 1, fontSize: '0.75rem', letterSpacing: '0.05em' }}>
              EDITOR
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mr: 1 }}>
              <Typography variant="caption" color="text.secondary">Preview:</Typography>
              <IconButton
                size="small"
                onClick={() => setPreviewDevice('mobile')}
                color={previewDevice === 'mobile' ? 'primary' : 'default'}
                title="Phone View"
              >
                <PhoneIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setPreviewDevice('desktop')}
                color={previewDevice === 'desktop' ? 'primary' : 'default'}
                title="Desktop View"
              >
                <ComputerIcon />
              </IconButton>
            </Box>

            {/* Add Page Button - Always visible */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addPage}
              color="primary"
              sx={{ py: 0.4, px: 1.2, fontSize: '0.75rem' }}
              title="Add a new page for elements"
            >
              Add page
            </Button>

            {/* Delete Page Button - Show when pagination is enabled */}
            {paginationEnabled && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => deletePage(currentPageIndex)}
                color="error"
                sx={{ py: 0.4, px: 1.2, fontSize: '0.75rem' }}
                title={pages.length > 1 ? "Delete the current page" : "Clear the current page"}
              >
                Delete page
              </Button>
            )}

            {/* Save Element / Update Element Button - Only show in Brands page */}
            {!templateSelectionOnly && isBrandsPage && (
              <Button
                type="button"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (!selectedBrand || !selectedOffer) {
                    setShowBrandOfferAlert(true);
                    return;
                  }

                  // If updating, directly save without showing dialog
                  if (isUpdatingElement) {
                    saveCurrentElement();
                  } else {
                    // If creating new, show dialog
                    setShowSaveElementDialog(true);
                  }
                }}
                disabled={!selectedElement}
                size="small"
                sx={{
                  py: 0.4,
                  px: 1.2,
                  fontSize: '0.75rem',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  minHeight: 'auto',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: '100px',
                  maxWidth: '200px'
                }}
                title={isUpdatingElement ? `Update "${currentElementName}"` : 'Save current element to library'}
              >
                {isUpdatingElement ? `Update "${currentElementName}"` : 'Save Element'}
              </Button>
            )}

            {/* Undo Buttons - Moved from canvas to toolbar */}
            {showUndoNotification && deletedElementsHistory.length > 0 && (
              <Box
                sx={{
                  backgroundColor: '#2e7d32',
                  color: 'white',
                  borderRadius: '50px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  animation: 'slideInRight 0.3s ease-out',
                  '@keyframes slideInRight': {
                    from: { transform: 'translateX(100%)', opacity: 0 },
                    to: { transform: 'translateX(0)', opacity: 1 }
                  },
                  '&:hover': {
                    backgroundColor: '#1b5e20',
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease'
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'white' }}>
                  Undo ({deletedElementsHistory.length})
                </Typography>
                <Button
                  size="small"
                  onClick={undoDelete}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    borderRadius: '20px'
                  }}
                >
                  Undo delete
                </Button>
                <IconButton
                  size="small"
                  onClick={dismissUndoNotification}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                    width: 24,
                    height: 24,
                    ml: 0.5
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}




            {/* Add Elements Button for Desktop - Only show in template creation context */}
            {!isMobile && !templatesOnly && (
              <Button
                variant={drawerOpen ? 'contained' : 'outlined'}
                size="small"
                startIcon={drawerOpen ? <CloseIcon /> : <AddIcon />}
                onClick={() => setDrawerOpen(!drawerOpen)}
                color={drawerOpen ? 'secondary' : 'primary'}
                sx={{ py: 0.4, px: 1.2, fontSize: '0.75rem', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {drawerOpen ? 'Hide Side Bar' : 'Show Side Bar'}
              </Button>
            )}

            {!hideLoadTemplate && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<TemplateIcon />}
                onClick={() => setShowTemplatesList(true)}
                sx={{ py: 0.4, px: 1.2, fontSize: '0.75rem', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Load Template
              </Button>
            )}

            {!templatesOnly && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={() => setShowSaveDialog(true)}
                  disabled={elements.length === 0}
                  title={elements.length === 0 ? 'Add elements to enable saving' : 'Save template with type and subtype'}
                  sx={{ py: 0.4, px: 1.2, fontSize: '0.75rem', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {currentTemplateId ? 'Update Template' : 'Save Template'}
                </Button>

                {/* New Template Button */}
                {currentTemplateId && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setConfirmDialogTitle('Start New Template');
                      setConfirmDialogMessage('Start a new template? This will clear your current work.');
                      setConfirmAction(() => () => {
                        createNewTemplate();
                      });
                      setShowConfirmDialog(true);
                    }}
                    sx={{ ml: 1, py: 0.4, px: 1.2, fontSize: '0.75rem', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    New Template
                  </Button>
                )}
              </>
            )}

            {/* Right-aligned buttons */}
            <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {/* Editing Title - Shows which item is being edited */}
              {selectedElement && (
                <Box sx={{
                  mr: 2,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0'
                }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.75rem'
                  }}>
                    <EditIcon fontSize="small" />
                    {isUpdatingElement && currentElementName
                      ? `Editing: "${currentElementName}"`
                      : `Editing: unsaved item`
                    }
                  </Typography>
                </Box>
              )}



            </Box>

          </Box>

          {/* Canvas area — phones rendered here, below the toolbar */}
          {children && (
            <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', minWidth: 0 }}>
              {children}
            </Box>
          )}

        </Box>
    </>
  );
};

export default LeftPanel;
